import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, Radar } from "lucide-mui";
import { useMemo } from "react";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import { api } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import CenteredSpinner from "../../../components/CenteredSpinner.tsx";
import FilterBar from "../../../components/filters/FilterBar.tsx";
import type { FilterSpec } from "../../../components/filters/specs.ts";
import { useFilters } from "../../../components/filters/useFilters.ts";
import Panel from "../../../components/Panel.tsx";
import { useTenant } from "../../../contexts/TenantContext.tsx";
import { useUrlState } from "../../../hooks/useUrlState.ts";
import { periodFrom, periodSpec, sinceFor } from "../../../utils/periods.ts";
import FunnelBars from "../../../components/charts/FunnelBars.tsx";
import KpiTile from "../../../components/KpiTile.tsx";
import SegmentedBar from "../../../components/charts/SegmentedBar.tsx";
import SignalsTimelineChart, { timelinePoints } from "./SignalsTimelineChart.tsx";
import { formatNumber, percent } from "../../../utils/format.ts";

const VERDICT_COLORS = {
  verified: "var(--mui-palette-success-main)",
  qualified: "var(--mui-palette-secondary-main)",
  inconclusive: "var(--mui-palette-text-disabled)",
  disqualified: "var(--mui-palette-error-main)",
};

/**
 * How the tenant's signal workflows perform: how many records they matched,
 * how many research qualified, matches over time and the research verdicts.
 * Every number opens the signal log behind it. What waits for a decision is
 * in Prospects.
 */
export default function SignalPerformancePage() {
  const { selectedTenantId, tenantPath } = useTenant();
  const url = useUrlState();
  const { searchParams } = url;

  const workflowId = searchParams.get("workflow") ?? "";
  const period = periodFrom(searchParams.get("period"));
  const since = sinceFor(period);

  const { data: workflows = [] } = useQuery({
    queryKey: keys.workflow.list(selectedTenantId, "signal", null),
    queryFn: () => api.getWorkflows({ tenant_id: selectedTenantId ?? undefined, trigger_type: "signal" }),
    enabled: !!selectedTenantId,
  });
  const { data, isLoading } = useQuery({
    queryKey: keys.signal.dashboard(selectedTenantId, workflowId || null, since ?? null),
    queryFn: () =>
      api.getSignalDashboard({
        tenant_id: selectedTenantId!,
        workflow_id: workflowId || undefined,
        since,
      }),
    enabled: !!selectedTenantId,
    meta: { live: true },
  });

  const specs = useMemo<FilterSpec[]>(
    () => [
      {
        kind: "options",
        key: "workflow",
        label: "Workflow",
        param: "workflow",
        multiple: false,
        pinned: true,
        noneLabel: "All workflows",
        options: workflows.map((w) => ({ value: w.id, label: w.name })),
        searchPlaceholder: "Search workflows",
      },
      periodSpec({ pinned: false }),
    ],
    [workflows],
  );
  const filters = useFilters(specs, { state: url });

  // Every tile links to the signals it counted, in the log: the same workflow
  // and period, any match strength, plus its own filter.
  const logLink = (extra: Record<string, string>) => {
    const q = new URLSearchParams({ min_rating: "all" });
    if (workflowId) q.set("workflow", workflowId);
    if (since) q.set("since", since);
    for (const [k, v] of Object.entries(extra)) q.set(k, v);
    return tenantPath(`/workflows/signals?${q.toString()}`);
  };

  // A backend older than this page (mid-rollout) sends no timeline.
  const bucket = data?.timeline_bucket ?? "day";
  const points = data ? timelinePoints(data.timeline ?? [], bucket, since) : [];

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <FilterBar filters={filters} variant="inline" />
      </Box>

      {isLoading && <CenteredSpinner />}
      {data && (
        <Stack spacing={2.5}>
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 6 }}>
              <KpiTile
                label="Matched"
                icon={<Radar />}
                value={data.matched}
                caption={
                  data.evaluated
                    ? `${percent(data.matched, data.evaluated)} of ${formatNumber(data.evaluated)} records evaluated`
                    : "No records evaluated yet"
                }
                to={logLink({ verdict: "all" })}
              >
                <LinearProgress
                  variant="determinate"
                  value={data.evaluated ? Math.min((data.matched / data.evaluated) * 100, 100) : 0}
                  sx={{ height: 6, borderRadius: 3, bgcolor: "action.hover" }}
                />
              </KpiTile>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <KpiTile
                label="Qualified or verified"
                icon={<BadgeCheck />}
                value={data.qualified_or_verified}
                emphasis
                caption={
                  data.researched
                    ? `${percent(data.qualified_or_verified, data.researched)} of ${formatNumber(data.researched)} researched`
                    : "Nothing researched yet"
                }
                to={logLink({ verdict: "verified,qualified" })}
              >
                {points.length > 1 && data.qualified_or_verified > 0 && (
                  <Box sx={{ height: 36, mx: -0.5 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={points} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                        <Area
                          type="monotone"
                          dataKey="qualified"
                          stroke="var(--mui-palette-primary-main)"
                          fill="var(--mui-palette-primary-main)"
                          fillOpacity={0.15}
                          strokeWidth={2}
                          dot={false}
                          isAnimationActive={false}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </KpiTile>
            </Grid>
          </Grid>

          <Panel
            title="Matches over time"
            subtitle={bucket === "week" ? "Signals matched per week" : "Signals matched per day"}
          >
            {data.matched === 0 ? (
              <Typography variant="body2" sx={{ color: "text.secondary", py: 6, textAlign: "center" }}>
                No signals matched in this period.
              </Typography>
            ) : (
              <SignalsTimelineChart points={points} bucket={bucket} />
            )}
          </Panel>

          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Panel fill title="Research funnel" subtitle="From match to a buyer worth approaching">
                <FunnelBars
                  stages={[
                    { label: "Matched", value: data.matched, to: logLink({ verdict: "all" }) },
                    {
                      label: "Researched",
                      value: data.researched,
                      to: logLink({ verdict: "verified,qualified,disqualified,inconclusive" }),
                    },
                    {
                      label: "Qualified or verified",
                      value: data.qualified_or_verified,
                      to: logLink({ verdict: "verified,qualified" }),
                    },
                  ]}
                />
              </Panel>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Panel fill title="Research verdicts" subtitle="What the research agent concluded">
                <SegmentedBar
                  emptyLabel="Nothing researched yet"
                  segments={(["verified", "qualified", "inconclusive", "disqualified"] as const).map((v) => ({
                    key: v,
                    label: v.charAt(0).toUpperCase() + v.slice(1),
                    value: data.by_verdict[v],
                    color: VERDICT_COLORS[v],
                    to: logLink({ verdict: v }),
                  }))}
                />
              </Panel>
            </Grid>
          </Grid>
        </Stack>
      )}
    </>
  );
}
